import { Box } from '@atgfr8/atg-ui-tailwind';
import { BasicModal, Button } from '@components';
import { useNavigate } from '@solidjs/router';
import { getLTLFalveyPricingQuotes } from '@store/ltl/actions';
import { closeModal, openModal } from '@store/modals';
import {
  FalveyInsuranceQuote,
  LtlQuoteParametersViewModel,
  LtlQuoteViewModel,
} from '@store/orders/types';
import { PlusCircleOutlineIcon, CheckCircleOutlineIcon } from '@atgfr8/icons';
import { routeOrderDetailsQuote } from '@utils/routeOrderDetails';
import { Show } from 'solid-js';

import { InsuranceUpdateAndConfirmationForm } from '../InsuranceUpdateAndConfirmation';

export const QuoteActionDetailsLegacy = (props: {
  quoteParameters: LtlQuoteParametersViewModel;
  quote: LtlQuoteViewModel;
  insuranceQuote?: FalveyInsuranceQuote;
  customerId: number;
  isInsuranceRequest: boolean;
  testMode: boolean;
}) => {
  const navigate = useNavigate();

  const handleSelectQuote = async (
    deleteQuote: boolean,
    hasInsurance: boolean,
  ) => {
    if (deleteQuote && hasInsurance) {
      await getLTLFalveyPricingQuotes(
        { declaredValue: 0, quoteParametersId: props.quoteParameters.id },
        true,
      );
    }
    const isNative = routeOrderDetailsQuote(Number(props.quote.id));
    if (isNative) {
      const path = `/order/details?quoteId=${props.quote.id}`;
      navigate(path);
    }
  };

  const addInsurranceModelId = props.quote.id.toString();
  const hasInsurance = (props.insuranceQuote?.premium ?? 0) > 0;

  return (
    <Box class="w-full">
      <Box class="flex w-full mt-4 gap-2 flex-wrap">
        <Show when={!props.isInsuranceRequest}>
          {!props.testMode && (
            <Button
              variant="contained"
              startIcon={<CheckCircleOutlineIcon size={20} />}
              class="bg-[#248670]! min-w-0 flex-1 whitespace-normal xl:whitespace-nowrap"
              label="Select Quote"
              onClick={async () => {
                await handleSelectQuote(true, hasInsurance);
              }}
              dataTestId={'select-quote-button'}
            />
          )}
        </Show>
        <Show when={!props.isInsuranceRequest && !hasInsurance}>
          {!props.testMode && (
            <Button
              variant="contained"
              class="bg-[#066da1]! min-w-0 flex-1 whitespace-normal xl:whitespace-nowrap"
              label="Add Insurance"
              startIcon={<PlusCircleOutlineIcon size={20} />}
              disabled={props.customerId === 0}
              onClick={() => openModal(addInsurranceModelId)}
              dataTestId={'add-insurance-button'}
            />
          )}
        </Show>
        <Show when={props.isInsuranceRequest && hasInsurance}>
          <Button
            variant="contained"
            startIcon={<CheckCircleOutlineIcon size={20} />}
            class="bg-[#248670]! w-full whitespace-normal xl:whitespace-nowrap"
            label="Select Insurance Quote"
            onClick={() => openModal(props.quote.id.toString())}
            dataTestId={'select-insurance-quote-button'}
          />
        </Show>
      </Box>
      <BasicModal
        id={addInsurranceModelId}
        width={'650px'}
        footer={false}
        header={false}
      >
        <InsuranceUpdateAndConfirmationForm
          items={props.quoteParameters.loadItems ?? []}
          onConfirmation={async () => {
            await handleSelectQuote(false, hasInsurance);
            closeModal(addInsurranceModelId);
          }}
          onCancel={() => {
            closeModal(addInsurranceModelId);
          }}
          declaredValue={props.quoteParameters.ltlInsuranceQuote?.declaredValue}
          customerCost={props.quote.customerTotal}
          quoteParametersId={props.quoteParameters.id}
          carrierCost={props.quote.carrierTotal}
          updateDeclaredValue={() => {
            /* noop, declared value saved in modal */
          }}
          falveyInsuranceQuote={{
            quotePremium: props.quoteParameters.ltlInsuranceQuote?.premium ?? 0,
            quoteServiceFee:
              props.quoteParameters.ltlInsuranceQuote?.serviceFee ?? 0,
            shippingSumInsured:
              props.quoteParameters.ltlInsuranceQuote?.declaredValue ?? 0,
          }}
        />
      </BasicModal>
    </Box>
  );
};
